import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { createItem, listItems, listUoms } from '../../api/masterData';
import { listStockBalances } from '../../api/inventory';
import { listWarehouses } from '../../api/orgData';
import { usePermissions } from '../../context/PermissionContext';
import {
  BusinessText,
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Item, StockBalance, Uom, Warehouse } from '../../types/domain';

/**
 * Items (Master Data) — first screen of the vertical slice. Read (RLS-scoped
 * via PostgREST) + create (fn_create_master_item, migration 0032,
 * permission-gated on item.create) — no other business logic in the
 * browser.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function ItemsPage() {
  const { context, hasPermission } = usePermissions();
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [stockedFilter, setStockedFilter] = useState('ALL');

  const canCreate = hasPermission('item', 'create');
  const canReadBalance = hasPermission('stock_balance', 'read');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [itemRows, uomRows, balanceRows, warehouseRows] = await Promise.all([
        listItems(),
        listUoms(),
        canReadBalance ? listStockBalances() : Promise.resolve([]),
        canReadBalance ? listWarehouses() : Promise.resolve([]),
      ]);
      setItems(itemRows);
      setUoms(uomRows);
      setBalances(balanceRows);
      setWarehouses(warehouseRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadBalance]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !it.item_code.toLowerCase().includes(q) && !it.name.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && it.status !== statusFilter) return false;
      if (stockedFilter !== 'ALL' && it.is_stocked !== (stockedFilter === 'YES')) return false;
      return true;
    });
  }, [items, search, statusFilter, stockedFilter]);

  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Items" subtitle="Master data barang — dibaca &amp; ditulis langsung dari packages/db (migration 0006/0032)." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Item baru'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewItemForm
          uoms={uoms}
          tenantId={context && context.authenticated ? context.tenant_id : ''}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Item', value: items.length },
              { label: 'Aktif', value: items.filter((it) => it.status === 'ACTIVE').length, tone: 'success' },
              { label: 'Nonaktif', value: items.filter((it) => it.status === 'INACTIVE').length, tone: 'neutral' },
              { label: 'Stocked', value: items.filter((it) => it.is_stocked).length, tone: 'primary' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari kode atau nama item..."
            resultCount={total}
            filters={
              <>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
                  <option value="ALL">Semua status</option>
                  <option value="ACTIVE">Aktif</option>
                  <option value="INACTIVE">Nonaktif</option>
                </Select>
                <Select value={stockedFilter} onChange={(e) => setStockedFilter(e.target.value)} style={{ width: 150 }}>
                  <option value="ALL">Semua item</option>
                  <option value="YES">Stocked</option>
                  <option value="NO">Non-stocked</option>
                </Select>
              </>
            }
          />
          {items.length === 0 && <EmptyState text="Belum ada item." />}
          {items.length > 0 && total === 0 && <EmptyState text="Tidak ada item yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Kode</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Stocked</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((it) => (
                  <ClickableRow key={it.item_id} onClick={() => setSelectedItem(it)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{it.item_code}</td>
                    <td style={tdStyle}><BusinessText text={it.name} /></td>
                    <td style={tdStyle}>{it.is_stocked ? 'Ya' : 'Tidak'}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={it.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <ItemDetailDrawer item={selectedItem} balances={balances} uoms={uoms} warehouses={warehouses} canReadBalance={canReadBalance} onClose={() => setSelectedItem(null)} />
    </div>
  );
}

function ItemDetailDrawer({
  item,
  balances,
  uoms,
  warehouses,
  canReadBalance,
  onClose,
}: {
  item: Item | null;
  balances: StockBalance[];
  uoms: Uom[];
  warehouses: Warehouse[];
  canReadBalance: boolean;
  onClose: () => void;
}) {
  const baseUom = item ? uoms.find((u) => u.uom_id === item.base_uom_id) : undefined;
  const itemBalances = item ? balances.filter((b) => b.item_id === item.item_id) : [];
  const warehouseName = (id: string) => warehouses.find((w) => w.warehouse_id === id)?.name ?? id;

  return (
    <Drawer open={!!item} onClose={onClose} title={item ? `${item.item_code} — ${item.name}` : 'Detail Item'}>
      {item && (
        <div>
          <DrawerField label="Kode" value={item.item_code} />
          <DrawerField label="Nama" value={<BusinessText text={item.name} />} />
          <DrawerField label="UOM dasar" value={baseUom?.code ?? item.base_uom_id} />
          <DrawerField label="Stocked" value={item.is_stocked ? 'Ya' : 'Tidak'} />
          <DrawerField label="Status" value={<StatusBadge status={item.status} />} />
          <DrawerField label="Dibuat" value={formatDateTime(item.created_at)} />

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Saldo Stok</div>
          {!canReadBalance && <p style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-faint)' }}>Anda tidak memiliki izin baca Stock Balance.</p>}
          {canReadBalance && itemBalances.length === 0 && <EmptyState text="Tidak ada saldo stok untuk item ini." variant="compact" />}
          {canReadBalance && itemBalances.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Gudang</th>
                  <th style={thStyle}>Qty on hand</th>
                </tr>
              </thead>
              <tbody>
                {itemBalances.map((b) => (
                  <tr key={b.warehouse_id}>
                    <td style={tdStyle}>{warehouseName(b.warehouse_id)}</td>
                    <td style={tdStyle}>{b.qty_on_hand}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {canReadBalance && itemBalances.length > 0 && (
            <Link to={`/app/stock-ledger?item=${item.item_id}`} style={{ display: 'inline-block', marginTop: 10, color: 'var(--alex-primary)', fontWeight: 600, fontSize: 'var(--alex-fs-caption)', textDecoration: 'none' }}>
              Lihat pergerakan stok item ini →
            </Link>
          )}
        </div>
      )}
    </Drawer>
  );
}

function NewItemForm({ uoms, tenantId, onCreated }: { uoms: Uom[]; tenantId: string; onCreated: () => void }) {
  const [itemCode, setItemCode] = useState('');
  const [name, setName] = useState('');
  const [baseUomId, setBaseUomId] = useState('');
  const [isStocked, setIsStocked] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createItem({ tenantId, itemCode, name, baseUomId, isStocked });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 20, padding: 16 }}>
      <div style={{ width: 160 }}>
        <FieldLabel label="Kode">
          <TextInput value={itemCode} onChange={(e) => setItemCode(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 220 }}>
        <FieldLabel label="Nama">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 140 }}>
        <FieldLabel label="UOM dasar">
          <Select value={baseUomId} onChange={(e) => setBaseUomId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {uoms.map((u) => (
              <option key={u.uom_id} value={u.uom_id}>
                {u.code}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <label style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 10 }}>
        <input type="checkbox" checked={isStocked} onChange={(e) => setIsStocked(e.target.checked)} /> Stocked
      </label>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
